import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
    Mail, 
    Phone, 
    Building, 
    Calendar, 
    RefreshCw,
    Inbox,
    Lock,
    LogOut,
    Eye,
    EyeOff
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPage = () => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [token, setToken] = useState(null);
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [loginError, setLoginError] = useState(null);
    const [loginLoading, setLoginLoading] = useState(false);
    
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Check for existing token on mount
    useEffect(() => {
        const savedToken = sessionStorage.getItem("admin_token");
        if (savedToken) {
            setToken(savedToken);
            setIsAuthenticated(true);
        }
    }, []);

    // Fetch messages when authenticated + auto-refresh every 15 seconds
    useEffect(() => {
        if (isAuthenticated && token) {
            fetchMessages();
            const interval = setInterval(() => {
                fetchMessages();
            }, 15000);
            return () => clearInterval(interval);
        }
    }, [isAuthenticated, token]);

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoginError(null);
        setLoginLoading(true);
        
        try {
            const response = await axios.post(`${API}/admin/login`, { password });
            if (response.data.token) {
                setToken(response.data.token);
                sessionStorage.setItem("admin_token", response.data.token);
                setIsAuthenticated(true);
                setPassword("");
            }
        } catch (err) {
            if (err.response?.status === 401) {
                setLoginError("Väärä salasana. Yritä uudelleen.");
            } else {
                setLoginError("Kirjautuminen epäonnistui. Yritä uudelleen.");
            }
        } finally {
            setLoginLoading(false);
        }
    };

    const handleLogout = async () => {
        try {
            await axios.post(`${API}/admin/logout`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
        } catch (err) {
            // Continue with logout even if API fails
        }
        sessionStorage.removeItem("admin_token");
        setToken(null);
        setIsAuthenticated(false);
        setMessages([]);
    };

    const fetchMessages = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get(`${API}/contact`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setMessages(response.data);
        } catch (err) {
            if (err.response?.status === 401) {
                // Token expired
                handleLogout();
                setLoginError("Istunto vanhentunut. Kirjaudu uudelleen.");
            } else {
                setError("Viestien lataus epäonnistui");
            }
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('fi-FI', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Login Screen
    if (!isAuthenticated) {
        return (
            <div className="min-h-screen bg-[#1a1a1a] flex items-center justify-center px-4">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                    className="w-full max-w-md"
                >
                    <Card className="border-0 shadow-2xl">
                        <CardContent className="p-8">
                            <div className="text-center mb-8">
                                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#22C55E]/10 rounded-full mb-4">
                                    <Lock className="h-8 w-8 text-[#22C55E]" />
                                </div>
                                <h1 className="text-2xl font-semibold text-slate-900 mb-2">
                                    Ylläpito
                                </h1>
                                <p className="text-slate-500 text-sm">
                                    Kirjaudu sisään nähdäksesi viestit
                                </p>
                            </div>

                            <form onSubmit={handleLogin} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-2">
                                        Salasana
                                    </label>
                                    <div className="relative">
                                        <Input
                                            type={showPassword ? "text" : "password"}
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            placeholder="Syötä salasana"
                                            className="pr-10"
                                            required
                                            autoFocus
                                            data-testid="admin-password-input"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                        >
                                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                        </button>
                                    </div>
                                </div>

                                {loginError && (
                                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                        <p className="text-sm text-red-600">{loginError}</p>
                                    </div>
                                )}

                                <Button
                                    type="submit"
                                    disabled={loginLoading || !password}
                                    className="w-full bg-[#1a1a1a] hover:bg-black text-white py-3 rounded-lg"
                                    data-testid="admin-login-button"
                                >
                                    {loginLoading ? "Kirjaudutaan..." : "Kirjaudu sisään"}
                                </Button>
                            </form>
                        </CardContent>
                    </Card>
                </motion.div>
            </div>
        );
    }

    // Admin Dashboard
    return (
        <div className="min-h-screen bg-[#F8FAFC]">
            {/* Header */}
            <div className="bg-[#1a1a1a] text-white py-8">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-2xl font-semibold">Ylläpito</h1>
                            <p className="text-white/70">Yhteydenottopyynnöt</p>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button 
                                onClick={fetchMessages}
                                variant="outline"
                                className="border-white/30 text-white hover:bg-white/10"
                            >
                                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                                Päivitä
                            </Button>
                            <Button 
                                onClick={handleLogout}
                                variant="outline"
                                className="border-white/30 text-white hover:bg-white/10"
                                data-testid="admin-logout-button"
                            >
                                <LogOut className="h-4 w-4 mr-2" />
                                Kirjaudu ulos
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <Card>
                        <CardContent className="p-6">
                            <div className="flex items-center gap-4">
                                <div className="bg-green-100 p-3 rounded-xl">
                                    <Inbox className="h-6 w-6 text-green-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-slate-500">Viestejä yhteensä</p>
                                    <p className="text-2xl font-bold text-slate-900">{messages.length}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-6">
                            <div className="flex items-center gap-4">
                                <div className="bg-blue-100 p-3 rounded-xl">
                                    <Calendar className="h-6 w-6 text-blue-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-slate-500">Tänään</p>
                                    <p className="text-2xl font-bold text-slate-900">
                                        {messages.filter(m => {
                                            const today = new Date().toDateString();
                                            const msgDate = new Date(m.created_at).toDateString();
                                            return today === msgDate;
                                        }).length}
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-6">
                            <div className="flex items-center gap-4">
                                <div className="bg-purple-100 p-3 rounded-xl">
                                    <Building className="h-6 w-6 text-purple-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-slate-500">Yrityksiä</p>
                                    <p className="text-2xl font-bold text-slate-900">
                                        {messages.filter(m => m.company).length}
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Messages List */}
                {loading ? (
                    <div className="text-center py-12">
                        <RefreshCw className="h-8 w-8 animate-spin text-slate-400 mx-auto mb-4" />
                        <p className="text-slate-500">Ladataan viestejä...</p>
                    </div>
                ) : error ? (
                    <div className="text-center py-12">
                        <p className="text-red-500">{error}</p>
                        <Button onClick={fetchMessages} className="mt-4">
                            Yritä uudelleen
                        </Button>
                    </div>
                ) : messages.length === 0 ? (
                    <div className="text-center py-12 bg-white rounded-2xl">
                        <Inbox className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                        <p className="text-slate-500">Ei vielä yhteydenottopyyntöjä</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {messages.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map((message, index) => (
                            <motion.div
                                key={message.id}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.05 }}
                            >
                                <Card className="hover:shadow-md transition-shadow">
                                    <CardContent className="p-6">
                                        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                                            {/* Left: Contact Info */}
                                            <div className="flex-1">
                                                <div className="flex items-center gap-3 mb-3">
                                                    <div className="bg-[#1a1a1a] text-white w-10 h-10 rounded-full flex items-center justify-center font-semibold">
                                                        {message.name.charAt(0).toUpperCase()}
                                                    </div>
                                                    <div>
                                                        <h3 className="font-semibold text-slate-900">{message.name}</h3>
                                                        <p className="text-sm text-slate-500">{formatDate(message.created_at)}</p>
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
                                                    <div className="flex items-center gap-2 text-sm text-slate-600">
                                                        <Mail className="h-4 w-4 text-slate-400" />
                                                        <a href={`mailto:${message.email}`} className="hover:text-green-600">
                                                            {message.email}
                                                        </a>
                                                    </div>
                                                    {message.phone && (
                                                        <div className="flex items-center gap-2 text-sm text-slate-600">
                                                            <Phone className="h-4 w-4 text-slate-400" />
                                                            <a href={`tel:${message.phone}`} className="hover:text-green-600 font-semibold">
                                                                {message.phone}
                                                            </a>
                                                        </div>
                                                    )}
                                                    {message.company && (
                                                        <div className="flex items-center gap-2 text-sm text-slate-600">
                                                            <Building className="h-4 w-4 text-slate-400" />
                                                            {message.company}
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Message */}
                                                <div className="bg-slate-50 rounded-xl p-4">
                                                    <p className="text-slate-700 whitespace-pre-wrap">{message.message}</p>
                                                </div>
                                            </div>

                                            {/* Right: Actions */}
                                            <div className="flex lg:flex-col gap-2">
                                                {message.phone && (
                                                    <Button
                                                        size="sm"
                                                        className="bg-[#22C55E] hover:bg-green-600 text-white"
                                                        onClick={() => window.location.href = `tel:${message.phone}`}
                                                    >
                                                        <Phone className="h-4 w-4 mr-1" />
                                                        Soita
                                                    </Button>
                                                )}
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="text-green-600 border-green-200 hover:bg-green-50"
                                                    onClick={() => window.location.href = `mailto:${message.email}`}
                                                >
                                                    <Mail className="h-4 w-4 mr-1" />
                                                    Vastaa
                                                </Button>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminPage;
